import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button, Form, Row, Container, Offcanvas, Card } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import { useAuth } from '../../context/auth';
import ModalNovoAgendamento from '../../components/Modals/ModalNovoAgendamento';
import TopTitlePage from '../../components/TopTitlePage';
import ButtonLimparAndFiltrar from '../../components/ButtonLimparAndFiltrar';
import ButtonFilterAndSelect from '../../components/ButtonFilterAndSelect';
import ModalSearchTecnicos from '../../components/ModalSearch/ModalSearchTecnicos';
import Agendamentos from '../../services/Agendamentos';
import Tecnicos from '../../services/Tecnicos';
import Loading from '../../components/Loading'
import { startOfMonth, endOfMonth, format, addDays, getMonth, parseISO, parse, startOfWeek, getDay, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import InputPaciente from '../../components/Inputs/InputPaciente'
import InputTipoAtendimento from '../../components/Inputs/InputTipoAtendimento'
import InputTecnico from '../../components/Inputs/InputTecnico'
import { calcIdade } from '../../utilities/calcIdade'
import { prontuarioLabel } from '../../utilities/prontuarioLabel'
import './style.css'
import Status from '../../services/Status';
import { SweetAlert } from '../../components/sweetalert';
import { useMediaQuery } from '../../components/Agendamento/useMediaQuery';
import { useStoredChoice } from './useStoredChoice';
import { useSpaceOutside } from './useSpaceOutside';
import { useMaximized } from './useMaximized';
import AgendaViewSwitch from './AgendaViewSwitch';
import AgendaDayList from './AgendaDayList';
import AgendaListTabs from './AgendaListTabs';
import AgendaWeek from './AgendaWeek';
import { CALENDAR_END_HOUR, CALENDAR_START_HOUR, outOfHoursEvents } from './outOfHours';
import {
  LEGEND_KEY, LEGEND_STATES, LIST_MODES, LIST_MODE_KEY, LOAD_IDLE, NO_FILTERS, PHONE_QUERY, PHONE_VIEWS, PHONE_VIEW_KEY, TOTALS_KEY, TOTALS_STATES, chipFromSearch, filtersFromForm, isSameDay, keyMonth,
  listEntryDay, loadFailed, loadKey, loadRange, loadRequested, loadSucceeded, monthKey, monthSummary, searchWithChip, weekOf,
} from './agendaDay';
import { SLOT_MINUTES } from './weekGrid';

// date-fns no lugar do moment (que só a Agenda usava): ~60 KB a menos no bundle
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales: { 'pt-BR': ptBR } });

// Textos iguais aos do moment pt-br: dia da semana abreviado com acento ("sáb") e horários em 24 h
const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const formats = {
  dayFormat: (date, culture, local) => `${local.format(date, 'dd', culture)} ${WEEKDAYS[getDay(date)]}`,
  weekdayFormat: (date) => WEEKDAYS[getDay(date)],
  agendaDateFormat: (date, culture, local) => `${WEEKDAYS[getDay(date)]} ${local.format(date, 'MMM dd', culture)}`,
  eventTimeRangeStartFormat: ({ start }, culture, local) => `${local.format(start, 'HH:mm', culture)} – `,
  eventTimeRangeEndFormat: ({ end }, culture, local) => ` – ${local.format(end, 'HH:mm', culture)}`,
};

const chipFromUrl = () => chipFromSearch(window.location.search);

// Faixa "Fora do horário": só visual, sem foco no Tab e fora do leitor de tela (a biblioteca a faz botão, como os agendamentos)
function CalendarEventWrapper({ isBackgroundEvent, children }) {
  return isBackgroundEvent ? React.cloneElement(children, { tabIndex: -1, 'aria-hidden': true }) : children;
}
const calendarComponents = { eventWrapper: CalendarEventWrapper };

export default function Agenda() {
  const messages = {
    allDay: 'Dia Inteiro',
    previous: '<',
    next: '>',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda do mês',
    date: 'Data',
    time: 'Hora',
    event: 'Agendamento',
    work_week: 'Semana',
    noEventsInRange: 'Não há eventos neste intervalo',
    showMore: (total) => `+ (${total})`,
  }

  const { register, getValues, setValue, handleSubmit, reset, control } = useForm();
  const { user } = useAuth();

  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [showModalAgendamento, setShowModalAgendamento] = useState(false);
  const [showModalTecnico, setShowModalTecnico] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [resources, setResources] = useState([]);
  const [status, setStatus] = useState([]);
  const [lengthInCalendar, setLengthInCalendar] = useState(endOfMonth(new Date()).getDate() + 1);
  const [cards, setCards] = useState([]);
  const [monthTotal, setMonthTotal] = useState(0); // "Total de agendamentos" do Calendário: só o mês, sem as pontas
  const [doctors, setDoctors] = useState([]);
  const lastRequest = useRef(0);
  const lastResponse = useRef(null);

  // Celular e tablet em pé (até 992 px): a Agenda abre na Lista do dia. A escolha "Lista | Calendário" fica lembrada no aparelho
  const phone = useMediaQuery(PHONE_QUERY, false);
  const [phoneView, setPhoneView] = useStoredChoice(PHONE_VIEW_KEY, PHONE_VIEWS, 'list');
  const showList = phone && phoneView === 'list';
  // Abas de Horários (Diário | Semana), também lembradas no aparelho
  const [listMode, setListMode] = useStoredChoice(LIST_MODE_KEY, LIST_MODES, 'day');
  // Legenda de status (no computador e, pelo menu ⋮, no Calendário do celular): oculta por padrão, para sobrar altura para o
  // calendário. O botão Mostrar/Ocultar legenda grava a escolha no aparelho, e recarregar a página volta como estava
  const [legend, setLegend] = useStoredChoice(LEGEND_KEY, LEGEND_STATES, 'hidden');
  const legendShown = legend === 'shown';
  // Totalizadores (cartões de status com a quantidade do mês), no computador e no Calendário do celular: ocultos por padrão,
  // como a legenda. O botão Mostrar/Ocultar totalizadores grava a escolha no aparelho, e recarregar a página volta como estava
  const [totals, setTotals] = useStoredChoice(TOTALS_KEY, TOTALS_STATES, 'hidden');
  const totalsShown = totals === 'shown';
  // Calendário com o resto da tela, sem sobra embaixo (style.css): espaço da página fora dele, medido na tela
  const pageRef = useRef(null);
  // Calendário maximizado (botão no rodapé do cartão, só no computador; useMaximized.js): o cartão ocupa a janela, e o
  // espaço da página fora dele não é medido. Ao restaurar, mede de novo
  const calendarCardRef = useRef(null);
  const [calendarMaximized, toggleCalendarMaximized] = useMaximized(calendarCardRef, !phone);
  const calendarSpace = useSpaceOutside(pageRef, '.custom-calendar', !showList && !calendarMaximized);
  const showListRef = useRef(showList);
  const wasList = useRef(showList);
  const listDay = useRef(null); // dia da Lista ao sair dela: o calendário em "Mês" sem navegar volta para ele
  // Carregamento por chave (mês + filtros): a da resposta na tela e a do último pedido
  const loadedKey = useRef(null);
  const requestedKey = useRef(null);
  const [load, setLoad] = useState(LOAD_IDLE);
  // Filtros que valem para a busca: os do último Filtrar ou Limpar (e o doutor logado, na abertura). Mexer nos campos e
  // fechar sem Filtrar não muda a Agenda
  const appliedFilters = useRef(NO_FILTERS);
  // Ao fechar o Detalhes (ou o novo), o foco volta ao cartão (ou ao +): o modal só devolve o foco ao que estava focado
  // ao abrir, e no iPhone tocar num botão não dá foco a ele
  const focusTarget = useRef(null);
  const wasModalOpen = useRef(false);
  const [returnFocus, setReturnFocus] = useState(null);
  // Rolagem da Lista. Ao abrir já em hoje, só se a página ainda não foi rolada
  const [scrollRequest, setScrollRequest] = useState(() => (showList ? { kind: 'now', onlyIfTop: true } : null));
  // Doutor dos chips da Lista na URL (?tecnico_id=): recarregar a página volta filtrada nele; "Todos" deixa a URL sem o
  // parâmetro. Gravado com replaceState, como as abas do Dashboard e do cadastro do paciente: uma navegação do React Router
  // remontaria a página. Uma navegação de verdade (ex.: Agenda no menu) lê a URL de novo
  const location = useLocation();
  const [doctorChip, setDoctorChip] = useState(() => chipFromUrl());

  useEffect(() => {
    setDoctorChip(chipFromUrl());
  }, [location.key]);

  useEffect(() => {
    setValue('date', new Date())

    if (user.tecnico?.id) {
      // setValue('tecnico_id', user.tecnico.id)
      setValue('tecnico_id_filtro', user.tecnico?.id)
      setValue('tecnico_filtro', user.tecnico?.nome)
    }
    appliedFilters.current = filtersFromForm(getValues())

    listStatus();
    listAll();

    // Atualização automática: a cada 30 s e só com a aba visível (cada aba aberta consultava o mês inteiro a cada 10 s).
    // Ao voltar para a aba, atualiza na hora.
    const refresh = () => {
      if (document.visibilityState === 'visible') listAll(true, screenDate());
    };
    const interval = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Entrar na Lista (pela chave ou com a janela encolhendo até 992 px). Na saída, o calendário monta no dia da Lista
  useLayoutEffect(() => {
    showListRef.current = showList;
    if (showList && !wasList.current) enterList();
    if (!showList && wasList.current) {
      listDay.current = currentDate;
      // Mês da Lista com erro ou ainda chegando: o calendário pede de novo, com o "Carregando" e o alerta de sempre
      if (load.pending || load.error) listAll(false, screenDate());
    }
    wasList.current = showList;
  }, [showList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detalhes (ou novo) fechado na Lista: pede a volta do foco ao cartão (ou ao +) por alguns segundos
  useEffect(() => {
    if (wasModalOpen.current && !showModalAgendamento && showListRef.current) {
      setReturnFocus({ id: focusTarget.current, until: Date.now() + 5000 });
    }
    wasModalOpen.current = showModalAgendamento;
  }, [showModalAgendamento]);

  // Data da tela (o mês carregado): o formulário guarda a data do calendário e o dia da Lista
  function screenDate() {
    const value = getValues('date');
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date : new Date();
  }

  // Atualizar, Filtrar e Limpar carregam o mês da tela (antes, o listAll(1) sem data carregava sempre o mês atual)
  const reloadScreenMonth = (timer = 1) => listAll(timer, screenDate());

  // Limpar: o reset() apagava também a data da tela, e a atualização automática seguinte quebrava (RangeError).
  // Continua tirando o filtro padrão do doutor, como antes
  function resetFilters() {
    const date = getValues('date');
    reset();
    setValue('date', date);
    appliedFilters.current = NO_FILTERS;
  }

  // Dia da Lista: grava a data do formulário junto (a atualização de 30 s, o modal e o + leem getValues('date')).
  // Outro mês faz o mesmo GET do calendário. Compara com o último mês PEDIDO, e não com o carregado: se a resposta de um
  // mês deixado para trás chegar por último, ela é descartada, porque o pedido do mês da tela é mais novo
  function selectDay(date, scroll) {
    const day = startOfDay(date);
    setValue('date', day);
    setCurrentDate(day);
    setScrollRequest({ kind: scroll ?? (isSameDay(day, new Date()) ? 'now' : 'head') });
    if (monthKey(day) !== keyMonth(requestedKey.current)) listAll(false, day);
  }

  // O dia vem da visão do calendário ("Dia" e "Semana" mantêm o dia; "Mês" e "Agenda do mês" vão para hoje ou o dia 1;
  // "Mês" sem navegar volta ao dia da Lista), sempre no mês já carregado. A visão da página volta para "Mês", porque o
  // calendário sempre monta de novo em "Mês"
  function enterList() {
    const today = new Date();
    const day = listEntryDay(currentView, currentDate, today, listDay.current);
    setCurrentView('month');
    setValue('date', day);
    setCurrentDate(day);
    setScrollRequest({ kind: isSameDay(day, today) ? 'now' : 'head' });
  }

  // + da Lista: o dia escolhido, o 1º status da sequência e o doutor logado; com um chip escolhido, o doutor do chip
  function openNew(doctor) {
    focusTarget.current = null;
    handleShowModalAgendamento(true);
    if (doctor) {
      setValue('tecnico_id', doctor.id);
      setValue('tecnico', doctor.nome);
    }
  }

  // Horário livre tocado na Semana: o Novo agendamento com o dia, o início, o fim (15 min) e o doutor da coluna. Com o
  // Início e o Fim preenchidos, o primeiro horário livre do modal (useNextSlot) não age; a verificação roda como sempre
  function openNewAt(doctor, date, startMinutes) {
    const time = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    focusTarget.current = null;
    handleShowModalAgendamento(true);
    setValue('data_inicial', format(date, 'yyyy-MM-dd'));
    setValue('hora_inicial', time(startMinutes));
    setValue('hora_final', time(startMinutes + SLOT_MINUTES));
    setValue('tecnico_id', doctor.id);
    setValue('tecnico', doctor.nome);
  }

  // Aba de Horários: o mesmo dia; rola até "agora" quando a aba nova mostra hoje (na Semana, hoje na semana do dia)
  function changeListMode(mode) {
    const today = new Date();
    const showsToday = mode === 'week' ? weekOf(currentDate).some((date) => isSameDay(date, today)) : isSameDay(currentDate, today);
    setListMode(mode);
    setScrollRequest({ kind: showsToday ? 'now' : 'head' });
  }

  // Dia tocado no cabeçalho da Semana: abre no Diário
  function openDay(date) {
    setListMode('day');
    selectDay(date);
  }

  function selectDoctorChip(chip) {
    setDoctorChip(chip);
    window.history.replaceState(window.history.state, '', window.location.pathname + searchWithChip(window.location.search, chip));
  }

  const doneScroll = useCallback((request) => setScrollRequest((current) => (current === request ? null : current)), []);

  // O cartão é memo: a função que ele recebe para abrir os Detalhes não muda a cada render da página
  const showInfoModalRef = useRef(null);
  showInfoModalRef.current = showInfoModal;
  const openEvent = useCallback((event) => {
    focusTarget.current = event.id;
    showInfoModalRef.current(event);
  }, []);

  const handleShow = () => { setType('filtro'); setShow(true); }
  const handleClose = () => { setType(null); setShow(false); }
  const handleShowModalAgendamento = (newDate = true) => {
    setShowModalAgendamento(true);

    let localDateTime

    if (newDate === false) {

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');

      localDateTime = `${year}-${month}-${day}`;

    } else {

      const now = new Date(getValues('date'));
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');

      localDateTime = `${year}-${month}-${day}`;
      setValue('status', status[0]?.id ?? '');
    }

    setValue('data_inicial', localDateTime);
    setValue('hora_inicial', '00:00');
    setValue('hora_final', '00:00');

    // Só no agendamento novo: ao abrir um existente, o doutor dele é mantido
    if (newDate !== false && user.tecnico?.id) {
      setValue('tecnico_id', user.tecnico?.id)
      setValue('tecnico', user.tecnico?.nome)
    }
  };

  const handleCloseModalAgendamento = () => setShowModalAgendamento(false);
  const handleCloseModalTecnico = () => setShowModalTecnico(false);

  // A lista de status muda pouco: carrega uma vez, ao abrir a Agenda (antes, a cada troca de mês ou de visão).
  // Todos os status: com os 10 da primeira página, os demais ficavam fora do modal, do filtro e da legenda
  function listStatus() {
    Status.listAll('', '', 300)
      .then((data) => {
        setStatus(data.data.data)
      })
      .catch((error) => {
        SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Status');
      })
  }

  function listAll(timer = false, date = new Date()) {
    const list = showListRef.current

    // Na Lista, o esqueleto (ou o Atualizar girando) fica no lugar do "Carregando" de tela inteira
    if (timer === false && !list) setLoading(true);

    // Os filtros aplicados (Filtrar/Limpar), e não os campos do painel: um campo mexido e não aplicado ficava valendo na
    // carga seguinte (atualização de 30 s, troca de mês, salvar no modal), sem o ponto vermelho do Filtros
    // O mês com as semanas inteiras das pontas: a Semana (e o calendário em Semana e Mês) mostra dias de outro mês
    let newFiltro = {
      ...appliedFilters.current,
      ...loadRange(date),
    }

    // Mesmo mês e filtros da tela: atualização, e os cartões da Lista ficam. Outro mês ou filtro: a Lista mostra o esqueleto
    // até a resposta deste pedido, e nunca "Nenhum agendamento" com dados de outro mês ou de outro filtro
    const key = loadKey(date, newFiltro)
    const refresh = key === loadedKey.current
    requestedKey.current = key
    setLoad((prev) => loadRequested(prev, key, refresh, list, timer === true))

    // Só a resposta do pedido mais recente vale: a de um mês ou filtro anterior que chegue atrasada é descartada
    const request = ++lastRequest.current

    listDoctors();

    Agendamentos.listAll(newFiltro)
      .then((data) => {
        if (request !== lastRequest.current) return
        loadedKey.current = key

        // Mesma resposta da última vez (o 304 do cache de ETag devolve o mesmo objeto): nada a redesenhar
        if (data.data === lastResponse.current) {
          setLoad((prev) => loadSucceeded(prev, key))
          return setLoading(false)
        }
        lastResponse.current = data.data

        const events = data.data.data
        const newEvents = []
        const newResources = [];

        events.forEach((event) => {
          newEvents.push({
            id: event.id,
            title: `${event.paciente.nome} - ${event.tipo_atendimento.descricao} - ${event.tecnico.nome}`,
            start: new Date(Date.parse(event.data_agendamento_inicial)),
            end: new Date(Date.parse(event.data_agendamento_final)),
            hora_inicial: format(new Date(event.data_agendamento_inicial), 'HH:mm'),
            hora_final: format(new Date(event.data_agendamento_final), 'HH:mm'),
            status: event.status,
            paciente: event.paciente,
            tecnico: event.tecnico,
            tipo_atendimento: event.tipo_atendimento,
            prontuario: event.prontuario,
            resourceId: event.tecnico.id,
          });

          if (!newResources.find(r => r.id === event.tecnico.id)) {
            newResources.push({
              id: event.tecnico.id,
              title: event.tecnico.nome,
            });
          }
        });

        const orderMap = {
          "CLAUDIA ROSADO DE ARAUJO": 0,
          "MIGUEL ROSADO DE ARAUJO": 1,
          "GABRIEL DE CARVALHO FARIAS": 2
        };

        newResources.sort((a, b) => {
          return orderMap[a.title] - orderMap[b.title];
        });

        // Cartões de status e total só do mês: o pedido traz também as semanas das pontas (monthSummary)
        const summary = monthSummary(newEvents, date, data.data.analytics)
        setCards(summary.cards)
        setMonthTotal(summary.total)

        setResources(newResources);
        setAgendamentos(newEvents)
        setLoad((prev) => loadSucceeded(prev, key))
        setLoading(false);
      })
      .catch((error) => {
        if (request !== lastRequest.current) return

        // Sem o catch, um erro deixava o "Carregando" travado na tela.
        // Na atualização automática, não vira alerta: tenta de novo em 30 s. Na Lista, o aviso fica na própria Lista
        // (o erro do mês no lugar dos cartões, ou a faixa da atualização com os cartões na tela)
        setLoading(false)
        setLoad((prev) => loadFailed(prev, key, refresh))
        // O modo vale na hora da resposta: quem trocou para o Calendário com o pedido em andamento vê o alerta
        if (timer === false && !showListRef.current) SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Agenda');
      })
  }

  // Doutores com os dias de atendimento, para as faixas "Fora do horário". Vem junto de cada carga da Agenda: mudou o horário
  // no cadastro, a faixa muda na próxima atualização (o cache de ETag devolve o mesmo objeto, e nada é redesenhado).
  // Sem permissão ou com erro, o calendário fica sem as faixas, sem alerta: os agendamentos são o que importa
  function listDoctors() {
    Tecnicos.listForSelect()
      .then(({ data }) => setDoctors(data.data || []))
      .catch(() => setDoctors([]))
  }

  function getBrightness(hex) {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, '');

    // Parse the r, g, b values
    let r = parseInt(hex.substr(0, 2), 16);
    let g = parseInt(hex.substr(2, 2), 16);
    let b = parseInt(hex.substr(4, 2), 16);

    // Calculate the brightness
    return ((r * 299) + (g * 587) + (b * 114)) / 1000;
  }

  function isColorDark(hex) {
    const brightness = getBrightness(hex);
    return brightness < 128; // You can adjust this threshold as needed
  }

  const eventStyleGetter = (event) => {
    if (event.outOfHours) return { className: 'agenda-out-of-hours' };

    const backgroundColor = event.status.cor;
    const textColor = isColorDark(backgroundColor) ? 'white' : 'black';

    return {
      style: {
        backgroundColor: backgroundColor,
        color: textColor,
      },
    };
  };

  function showInfoModal(event) {
    if (event.outOfHours) return;

    setValue('id', event.id);
    setValue('paciente', event.paciente.nome + calcIdade(event.paciente.data_nascimento));
    setValue('paciente_id', event.paciente.id);
    setValue('tecnico', event.tecnico.nome);
    setValue('tecnico_id', event.tecnico.id);
    setValue('tipo_atendimento', event.tipo_atendimento.descricao);
    setValue('tipo_atendimento_id', event.tipo_atendimento.id);
    setValue('prontuario', prontuarioLabel(event.prontuario));
    setValue('prontuario_id', event.prontuario.id);
    setValue('status', event.status.id);
    setValue('telefone1', event.paciente.telefone);
    setValue('telefone2', event.paciente.telefone2);

    // const start = new Date(event.start);
    // const startTime = `${start.getFullYear()}-${(start.getMonth() + 1).toString().padStart(2, '0')}-${start.getDate().toString().padStart(2, '0')}`;
    setTimeout(() => {
      setValue('data_inicial', format(new Date(event.start), 'yyyy-MM-dd'));
      setValue('hora_inicial', event.hora_inicial);
      setValue('hora_final', event.hora_final);
    }, 100);

    handleShowModalAgendamento(false);
  }

  function onFilter() {
    appliedFilters.current = filtersFromForm(getValues())
    setFilter(true)
    setShow(false)
    listAll(1, screenDate())
  }

  const handleNavigate = (newDate) => {
    let dateCurrent = new Date()

    if(currentView === 'agenda') {
      const dataAjustada = addDays(newDate, 1)

      const dataPrimeiroDiaMes = startOfMonth(dataAjustada)
      dateCurrent = dataPrimeiroDiaMes

      let dataUltimoDiaMes = endOfMonth(dataPrimeiroDiaMes).getDate()
      if(dataUltimoDiaMes < 30) dataUltimoDiaMes++
      setLengthInCalendar(dataUltimoDiaMes - 1)
    } else {
      let dataUltimoDiaMes = endOfMonth(newDate).getDate()
      if(dataUltimoDiaMes < 30) dataUltimoDiaMes++
      setLengthInCalendar(dataUltimoDiaMes - 1)
      dateCurrent = newDate;
    }

    setValue('date', dateCurrent)
    setCurrentDate(dateCurrent);
    listAll(false, dateCurrent)
  };

  const handleViewChange = (newView) => {
    let dateCurrent = currentDate

    if(newView === 'agenda') {
      const dataPrimeiroDiaMes = startOfMonth(currentDate)
      dateCurrent = dataPrimeiroDiaMes

      let dataUltimoDiaMes = endOfMonth(dataPrimeiroDiaMes).getDate()
      if(dataUltimoDiaMes < 30)  dataUltimoDiaMes++
      setLengthInCalendar(dataUltimoDiaMes - 1)
    } else {
      let dataUltimoDiaMes = endOfMonth(currentDate).getDate()
      if(dataUltimoDiaMes < 30)  dataUltimoDiaMes++
      setLengthInCalendar(dataUltimoDiaMes - 1)      

      const mesDefinido = getMonth(currentDate)
      const mesAtual = getMonth(new Date())
      if(mesDefinido === mesAtual)
        dateCurrent = new Date()
      else
        dateCurrent = startOfMonth(currentDate)
    }      

    setCurrentView(newView);
    setValue('date', dateCurrent)
    setCurrentDate(dateCurrent)
    listAll(false, dateCurrent)
  }

  // Faixas do mês da tela (e das semanas que o cruzam). No calendário, só o Dia e a Semana mostram faixas de fundo; a Lista
  // do dia mostra as do dia escolhido
  const screenMonth = monthKey(currentDate);
  const outOfHours = useMemo(
    () => outOfHoursEvents(doctors, currentDate, CALENDAR_START_HOUR, CALENDAR_END_HOUR),
    [doctors, screenMonth], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Lista: pronta só com a resposta do mês do dia escolhido (e dos filtros do último pedido)
  const listState = load.error ? 'error'
    : (load.pending || !load.key || keyMonth(load.key) !== monthKey(currentDate)) ? 'loading' : 'ready';

  // Semana: doutores que podem receber agendamento viram coluna mesmo sem agendamento no mês (para o toque no horário
  // livre): ativos, com horário de atendimento e, com o filtro de doutor aplicado (ou no login de doutor), só ele
  const doctorFilter = appliedFilters.current.tecnico_id;
  const bookableDoctors = useMemo(() => doctors.filter((doctor) => doctor.ativo !== false
    && Array.isArray(doctor.service_hours) && doctor.service_hours.length > 0
    && (!doctorFilter || doctor.id === doctorFilter)), [doctors, doctorFilter]);

  return (
    <>
      {/* Com o calendário maximizado, o "Carregando" fica dentro do cartão: aqui ele ficaria atrás dele */}
      {loading === true && !calendarMaximized ? <Loading /> : ''}
      {/* No Calendário do celular, sem a margem de baixo: o cartão do calendário vai até o fim da tela */}
      <div ref={pageRef} className={phone && !showList ? undefined : 'mb-3'} style={{ marginLeft: 20, marginRight: 20 }}>

        <Offcanvas show={show} onHide={handleClose} style={{ height: '100vh', backgroundColor: 'var(--app-panel-bg)' }} placement='bottom'>
          <Container>
            <Form onSubmit={handleSubmit(onFilter)}>
              <Offcanvas.Header closeButton>
                <TopTitlePage title='Filtro' />
              </Offcanvas.Header>
              <Offcanvas.Body>
                <Card className='cardShadow'>
                  <Card.Body className='p-3'>

                    <Row className='d-flex align-items-end'>
                      <InputPaciente
                        setValue={setValue}
                        loading={loading}
                        setLoading={setLoading}
                        getValues={getValues}
                        register={register}
                        registerNameId='paciente_id_filtro'
                        registerName='paciente_filtro'
                      />
                    </Row>

                    <Row className='d-flex align-items-end mt-2'>
                      <InputTecnico
                        setValue={setValue}
                        loading={loading}
                        setLoading={setLoading}
                        getValues={getValues}
                        register={register}
                        registerNameId='tecnico_id_filtro'
                        registerName='tecnico_filtro'
                      />

                    </Row>

                    <Row className='d-flex align-items-end'>
                      <InputTipoAtendimento
                        setValue={setValue}
                        loading={loading}
                        setLoading={setLoading}
                        getValues={getValues}
                        register={register}
                        registerNameId='tipo_atendimento_id_filtro'
                        registerName='tipo_atendimento_filtro'
                      />
                    </Row>

                    <Row className='d-flex align-items-end'>
                      <Form.Group className="col-12 col-lg-2">
                        <Form.Label>Status</Form.Label>
                        <Form.Select {...register('status_filtro')} size='sm'>
                          <option value="">TODOS</option>
                          {status?.map(item => {
                            return (
                              <option value={item.id}>{item.nome}</option>

                            )
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Row>

                  </Card.Body>
                </Card>
                <div className='d-flex justify-content-end mt-3 gap-3'>
                  <ButtonLimparAndFiltrar
                    handleClose={handleClose}
                    reset={resetFilters}
                    listAll={reloadScreenMonth}
                    setFilter={setFilter}
                  />
                </div>
              </Offcanvas.Body>
            </Form>
          </Container>
        </Offcanvas>

        {/* Celular: a chave e a Lista ficam fora do <Form> (um Enter na data não envia o formulário nem recarrega a página).
            Atualizar do menu (⋮), que fecha ao tocar: em Horários o botão do menu gira (refreshing), e o erro vira a faixa da
            Lista, como antes; no Calendário, o "Carregando" e o alerta de erro da troca de mês (com o listAll(1) nada aparecia).
            Legenda e totalizadores no menu do Calendário: a mesma escolha gravada do computador */}
        {phone ? (
          <AgendaViewSwitch
            view={phoneView}
            onChange={setPhoneView}
            filter={filter}
            onFilters={handleShow}
            refreshing={load.refreshing}
            onRefresh={() => listAll(false, screenDate())}
            onNew={showList ? undefined : () => handleShowModalAgendamento(true)}
            legendShown={legendShown}
            onToggleLegend={() => setLegend(legendShown ? 'hidden' : 'shown')}
            totalsShown={totalsShown}
            onToggleTotals={() => setTotals(totalsShown ? 'hidden' : 'shown')}
          />
        ) : ''}

        {/* Horários: abas Diário (a Lista do dia) e Semana. O painel tem os ids que o Tab.Container das abas usa */}
        {showList ? (
          <>
            <AgendaListTabs mode={listMode} onChange={changeListMode} />
            <div role='tabpanel' id={`agenda-list-tabpane-${listMode}`} aria-labelledby={`agenda-list-tab-${listMode}`}>
              {listMode === 'week' ? (
                <AgendaWeek
                  day={currentDate}
                  events={agendamentos}
                  outOfHours={outOfHours}
                  doctors={doctors}
                  bookable={bookableDoctors}
                  statusList={status}
                  state={listState}
                  refreshError={load.refreshError}
                  filtered={filter}
                  scrollRequest={scrollRequest}
                  onScrollDone={doneScroll}
                  returnFocus={returnFocus}
                  chip={doctorChip}
                  onChipChange={selectDoctorChip}
                  onSelectDay={selectDay}
                  onToday={() => selectDay(new Date(), 'now')}
                  onOpenDay={openDay}
                  onOpen={openEvent}
                  onNew={openNew}
                  onNewAt={openNewAt}
                  onFilters={handleShow}
                  onRetry={() => listAll(false, screenDate())}
                  onRefresh={() => reloadScreenMonth()}
                />
              ) : (
                <AgendaDayList
                  day={currentDate}
                  events={agendamentos}
                  outOfHours={outOfHours}
                  statusList={status}
                  state={listState}
                  refreshError={load.refreshError}
                  filtered={filter}
                  scrollRequest={scrollRequest}
                  onScrollDone={doneScroll}
                  returnFocus={returnFocus}
                  chip={doctorChip}
                  onChipChange={selectDoctorChip}
                  onSelectDay={selectDay}
                  onToday={() => selectDay(new Date(), 'now')}
                  onOpen={openEvent}
                  onNew={openNew}
                  onFilters={handleShow}
                  onRetry={() => listAll(false, screenDate())}
                  onRefresh={() => reloadScreenMonth()}
                />
              )}
            </div>
          </>
        ) : ''}

        <Form>

          <ModalNovoAgendamento
            show={showModalAgendamento}
            handleClose={handleCloseModalAgendamento}
            setAgendamentos={setAgendamentos}
            register={register}
            setValue={setValue}
            getValues={getValues}
            reset={reset}
            listAll={listAll}
            control={control}
            status={status}
            goToDate={(date) => (showList ? selectDay(parseISO(date)) : handleNavigate(currentView === 'agenda' ? startOfMonth(parseISO(date)) : parseISO(date)))}
          />

          <ModalSearchTecnicos
            show={showModalTecnico}
            handleClose={handleCloseModalTecnico}
            setValues={setValue}
            loading={loading}
            setLoading={setLoading}
            type={type}
          />

          {/* Calendário: a tela de sempre (no celular, pela chave "Calendário") */}
          {!showList ? (
          <div>

            {/* Totalizadores: cartões compactos, todos numa linha no computador e numa faixa que rola para o lado no celular
                (style.css). Ocultos por padrão, com o atributo hidden (o [hidden] do Bootstrap tem !important e vence o
                display: grid); o botão Mostrar totalizadores os mostra */}
            <div id='agenda-status-cards' className='cardContainerHome agenda-status-cards' hidden={!totalsShown}>

              {cards?.map(item => {
                return (
                  <div key={item.status} className="cardHome agenda-status-card">
                    <div className="agenda-status-icon" style={{ backgroundColor: `${item.cor}` }}>
                      {item.icon === null ? '' : <img src={item.icon} alt="icon" />}
                    </div>

                    <div className="d-flex flex-column">
                      <span className="caption">{item.status}</span>
                      <h3 className="cardText">{item.quantidade}</h3>
                    </div>
                  </div>
                )
              })}

            </div>

            {/* Botões e legenda na mesma linha (no computador), para sobrar altura para o calendário */}
            <div className='agenda-actions'>
              {/* No celular, Filtros e Atualizar ficam no menu ⋮ da linha da chave "Horários | Calendário", ao lado do + */}
              {!phone ? (
                <div className='d-flex gap-2 flex-column flex-md-row'>
                  <ButtonFilterAndSelect
                    handleShow={handleShow}
                    listAll={reloadScreenMonth}
                    filter={filter}
                  />
                  <Button onClick={() => handleShowModalAgendamento(true)}>Novo agendamento</Button>
                  {/* O texto diz o que o clique faz; o aria-expanded, se a legenda está na tela */}
                  <Button
                    variant='outline-secondary'
                    className='d-flex align-items-center gap-1'
                    aria-expanded={legendShown}
                    aria-controls='agenda-status-legend'
                    onClick={() => setLegend(legendShown ? 'hidden' : 'shown')}
                  >
                    {legendShown ? <><FiEyeOff /> Ocultar legenda</> : <><FiEye /> Mostrar legenda</>}
                  </Button>
                  {/* Do mesmo jeito, os totalizadores (cartões de status no topo) */}
                  <Button
                    variant='outline-secondary'
                    className='d-flex align-items-center gap-1'
                    aria-expanded={totalsShown}
                    aria-controls='agenda-status-cards'
                    onClick={() => setTotals(totalsShown ? 'hidden' : 'shown')}
                  >
                    {totalsShown ? <><FiEyeOff /> Ocultar totalizadores</> : <><FiEye /> Mostrar totalizadores</>}
                  </Button>
                </div>
              ) : ''}
              {/*
              <h2>Data Atual: {moment(currentDate).format('DD/MM/YYYY')}</h2>
              <h2>Visão Atual: {currentView}</h2> */}

              <div id='agenda-status-legend' className="container-legenda" hidden={!legendShown}>
                {status.map(item => {
                  const textColor = isColorDark(item.cor) ? 'white' : 'black';
                  return (
                    <div key={item.nome} className="item-legenda" style={{ backgroundColor: item.cor, color: textColor }}>
                      <p className="text-legenda">{item.nome}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Maximizado (useMaximized.js): o mesmo cartão ocupa a janela, por CSS (style.css). Outro calendário, num modal,
                montaria de novo e voltaria para "Mês" */}
            <Card
              ref={calendarCardRef}
              className={`cardShadow agenda-calendar-card ${calendarMaximized ? 'agenda-calendar-maximized' : 'mt-2'}`}
              role={calendarMaximized ? 'dialog' : undefined}
              aria-modal={calendarMaximized || undefined}
              aria-label={calendarMaximized ? 'Calendário maximizado' : undefined}
            >
              {loading === true && calendarMaximized ? <Loading /> : ''}
              {/* Sem p-3: o padding padrão do Card.Body já é 1rem, e no computador o style.css o reduz */}
              <Card.Body>
                <Calendar
                  className="custom-calendar"
                  length={lengthInCalendar}
                  views={["day", "agenda", "work_week", "month"]}
                  selectable
                  localizer={localizer}
                  defaultDate={new Date()}
                  defaultView="month"
                  events={agendamentos}
                  backgroundEvents={outOfHours}
                  components={calendarComponents}
                  style={calendarSpace === null ? undefined : { '--agenda-calendar-space': `${calendarSpace}px` }}
                  onSelectEvent={(event) => showInfoModal(event)}
                  culture={"pt-BR"}
                  messages={messages}
                  eventPropGetter={eventStyleGetter}
                  formats={formats}
                  step={7.5}
                  onNavigate={handleNavigate}
                  onView={handleViewChange}
                  min={new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), CALENDAR_START_HOUR)}
                  max={new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), CALENDAR_END_HOUR)}
                  // Abre o dia/semana no começo do expediente (com o moment era assim; o date-fns rolava até a hora atual)
                  scrollToTime={new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), CALENDAR_START_HOUR)}
                  resources={resources}
                  resourceIdAccessor="id"
                  resourceTitleAccessor="title"
                  showAllEvents={true}
                  date={currentDate}
                />
              </Card.Body>
              {/* Total à esquerda e, no computador, o botão que maximiza o calendário (o mesmo botão restaura; o Esc também) */}
              <Card.Footer className='d-flex align-items-center justify-content-between gap-2'>
                <Card.Title>
                  Total de agendamentos: {monthTotal}
                </Card.Title>
                {!phone ? (
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    className='agenda-maximize-button'
                    title={calendarMaximized ? 'Voltar o calendário ao tamanho normal (Esc)' : 'Mostrar o calendário na tela inteira'}
                    onClick={toggleCalendarMaximized}
                  >
                    {calendarMaximized
                      ? <><MdFullscreenExit size={16} aria-hidden='true' /> Restaurar</>
                      : <><MdFullscreen size={16} aria-hidden='true' /> Maximizar</>}
                  </Button>
                ) : ''}
              </Card.Footer>
            </Card>
          </div>
          ) : ''}
        </Form >
      </div >
    </>
  );
}
